import {
  Button,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Show } from '@pelicargo/ui';
import { ConditionalField, Filters } from '@pelicargo/utils';
import { FilterExpression } from '@prisma/client';
import { omit } from 'lodash';
import { JSX, useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HiMiniChevronDown, HiMiniChevronUp, HiMiniPencilSquare, HiPlus, HiTrash, HiXMark } from 'react-icons/hi2';
import z from 'zod';

import { trpc, TrpcOutputs } from '../config/trpc';
import { DynamicForm } from './DynamicForm';
import { Condition, FilterEditor } from './FilterEditor';
import { SimpleForm } from './SimpleForm';
import { SimpleInput } from './SimpleInput';

type EditorInfo<T extends z.ZodObject<{ name: z.ZodString }>> = {
  validator: T;
  name: string;
  description: string;
  reorderable?: boolean;
  overrides?: Record<keyof T['_def']['shape'], JSX.Element>;
  hiddenOverrides?: (keyof T['_def']['shape'])[];
};

const simpleValidator = z.object({
  name: z.string(),
});

const editors = {
  [Filters.SCOPES.requestClassification]: {
    validator: z.object({
      color: z.enum(['gray', 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'cyan', 'purple', 'pink'] as const),
      name: z.string(),
    }),
    name: 'Request Classification',
    description: 'Dermines the classification of requests. First match from the top determines the color/label',
    reorderable: true,
  },
  [Filters.SCOPES.moveToInProgress]: {
    validator: simpleValidator,
    name: 'Move To In Progress',
    description: 'Logic for determining to move to in progress. If any one of the following matches, it is moved',
  },
  [Filters.SCOPES.moveToQuotingComplete]: {
    validator: simpleValidator,
    name: 'Move To Quoting Complete',
    description:
      'Logic for determining to move a request to Quoting Complete. If a request matches any of the following, it is moved and a quote update email is sent to the customer.',
  },

  [Filters.SCOPES.quoteSelector]: {
    validator: simpleValidator,
    name: 'Quote Selectors',
    description: "Tools for automatically selecting quotes on a request's quotes list",
  },
  [Filters.SCOPES.shouldHandleFirstParty]: {
    validator: simpleValidator,
    name: 'API Filtering',
    description: 'Edit logic for determining whether or not to use the API for quoting',
    overrides: {
      name: <SimpleInput name="name" label="API Identifier" placeholder="AC, ECHO, FORWARDAIR, etc." />,
    },
    hiddenOverrides: ['name'],
  },
  [Filters.SCOPES.sellRateLineItems]: {
    validator: simpleValidator,
    name: 'Sell Rate Line Items',
    description: 'Individual logic for determining what is in a sell rate',
  },
  [Filters.SCOPES.shouldSearchForSystemFPCs]: {
    validator: simpleValidator,
    name: 'System Markups',
    description: 'Individual logic for deciding whether or not to include a system markup on a quote in Admin',
  },
  [Filters.SCOPES.conditionalFields]: {
    validator: z.object({
      name: z.nativeEnum(ConditionalField),
    }),
    name: 'Conditional Fields',
    description: 'Logic for determining specific fields to show to the customer',
  },
} satisfies Record<Filters.SCOPES, EditorInfo<any>>;

type FilterProps = {
  filter: FilterExpression;
  index: number;
  isLast: boolean;
  moveUp: () => void;
  moveDown: () => void;
  processing: boolean;
  onEdit: () => void;
  onRemove: () => void;
};

type BaseFilter = TrpcOutputs['getFilters'][0];

const Editor = ({
  isOpen,
  onClose,
  base,
  refetch,
  scope,
}: {
  isOpen: boolean;
  onClose: () => void;
  base: BaseFilter;
  refetch: () => void;
  scope: Filters.SCOPES;
}) => {
  const { mutateAsync, isLoading } = trpc.upsertGenericFilter.useMutation();
  const condition = useRef<Condition>();
  const schema = editors[scope].validator;

  const methods = useForm({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      name: base?.identifier,
      ...((base?.meta as any) ?? {}),
    },
  });

  const onValid = async (form: z.infer<typeof schema>) => {
    if (!condition.current) return;

    await mutateAsync({
      filter: condition.current.check(),
      name: form.name,
      variables: condition.current.getConfig()['enabledVariables'],
      scope,
      meta: omit(form, 'name'),
    });
    refetch();
    onClose();
  };

  const handleSubmit = methods.handleSubmit(onValid);

  return (
    <SimpleForm handleSubmit={handleSubmit} submitOnBlur={false} {...methods}>
      <Drawer size="full" isOpen={isOpen} onClose={onClose}>
        <DrawerOverlay />

        <DrawerContent>
          <DrawerHeader display="flex" justifyContent="space-between">
            <Heading>{editors[scope].name} Editor</Heading>{' '}
            <IconButton aria-label="close" onClick={onClose} icon={<HiXMark />} colorScheme="red" />
          </DrawerHeader>
          <DrawerBody>
            <FilterEditor
              rootRef={condition}
              base={base?.filter as Filters.CompiledGroupType}
              scope={scope}
              baseVariables={base?.variables?.reduce((agg, variable) => {
                agg[variable.name] = variable.discriminators;
                return agg;
              }, {})}
              allowVariables={false}
            />
          </DrawerBody>
          <DrawerFooter gap={2}>
            <DynamicForm
              def={schema._def as any} // Hate to do this but it won't be happy otherwise due to different name checks
              overrides={editors[scope]?.['overrides'] ?? {}}
              hiddenOverrides={editors[scope]?.['hiddenOverrides']}
              renderProps={{
                flexDirection: 'row',
                width: '40%',
              }}
            />
            <Button w={24} isLoading={isLoading} type="submit" onClick={handleSubmit} colorScheme="primary" h="full">
              Save
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </SimpleForm>
  );
};

const RenderFilter = ({ filter, index, processing, isLast, moveUp, moveDown, onEdit, onRemove }: FilterProps) => {
  return (
    <Flex gap={2} w="full">
      <Show if={editors[filter.scope].reorderable}>
        <Flex direction="column">
          <IconButton
            isDisabled={index === 0 || processing}
            height={4}
            borderBottomRadius={0}
            icon={<HiMiniChevronUp />}
            aria-label={'Move up'}
            onClick={moveUp}
          />
          <IconButton
            isDisabled={isLast || processing}
            height={4}
            borderTopRadius={0}
            icon={<HiMiniChevronDown />}
            aria-label={'Move down'}
            onClick={moveDown}
          />
        </Flex>
      </Show>
      <Text flexGrow={1}>{filter.identifier}</Text>
      <IconButton icon={<HiMiniPencilSquare />} isDisabled={processing} aria-label={'Edit'} onClick={onEdit} />
      <IconButton
        icon={<HiTrash />}
        isDisabled={processing}
        colorScheme="red"
        aria-label={'Delete'}
        onClick={onRemove}
      />
    </Flex>
  );
};

export const Listing = ({ scope }: { scope: Filters.SCOPES }) => {
  const { isLoading, data, refetch } = trpc.getFilters.useQuery({
    scope,
  });
  const { mutateAsync: reorderFilters, isLoading: isReordering } = trpc.reorderFilters.useMutation();
  const { mutateAsync: deleteFilter, isLoading: isDeleting } = trpc.deleteFilter.useMutation();

  const [selected, setSelected] = useState<BaseFilter>();

  const reorder = useCallback(
    async (origin: number, target: number) => {
      // we CS99 up in here
      const ids = data.map((x) => x.id);
      const tmp = ids[target];
      ids[target] = ids[origin];
      ids[origin] = tmp;
      await reorderFilters({
        ids,
      });
      await refetch();
    },
    [data, refetch, reorderFilters],
  );

  const disclosure = useDisclosure();

  const newFilter = useCallback(() => {
    setSelected(null);
    disclosure.onOpen();
  }, [disclosure]);

  if (isLoading) {
    return (
      <Center flex={1} h="full">
        <Spinner />
      </Center>
    );
  }

  return (
    <Stack w="full">
      <Show if={disclosure.isOpen}>
        <Editor {...disclosure} base={selected} refetch={refetch} scope={scope} />
      </Show>
      <Stack>
        {data?.map((filter, index) => (
          <RenderFilter
            key={filter.id}
            filter={filter}
            index={index}
            isLast={index === data?.length - 1}
            processing={isReordering || isDeleting || isLoading}
            moveUp={() => reorder(index, index - 1)}
            moveDown={() => reorder(index, index + 1)}
            onEdit={() => {
              setSelected(filter);
              disclosure.onOpen();
            }}
            onRemove={async () => {
              await deleteFilter({
                id: filter.id,
              });
              refetch();
            }}
          />
        ))}
        <Show if={!data?.length}>
          <Text>There are no filters for this scope yet.</Text>
        </Show>
        <Divider />
        <Button rightIcon={<HiPlus />} onClick={newFilter} colorScheme="primary">
          Add
        </Button>
      </Stack>
    </Stack>
  );
};
// A moment of silence for it's original name, OneFilterEditorToRuleThemAll
export const FilterCrud = () => {
  const [selectedScope, setSelectedScope] = useState<Filters.SCOPES>(Filters.SCOPES.requestClassification);

  const renderItem = ([scope, info]: [Filters.SCOPES, EditorInfo<any>]) => (
    <Stack
      key={scope}
      cursor="pointer"
      direction="column"
      bg={scope === selectedScope ? 'blackAlpha.200' : undefined}
      _hover={{ bg: 'blackAlpha.100' }}
      onClick={() => setSelectedScope(scope as Filters.SCOPES)}
      spacing={1.5}
      borderRadius="md"
      borderWidth={1}
      p={3}
      px={4}
    >
      <Text variant="heading" fontWeight="medium">
        {info.name}
      </Text>
      <Text fontSize="sm">{info.description}</Text>
    </Stack>
  );

  return (
    <Flex p={4} gap={4} overflowY="auto">
      <Stack flex="2" height="full" overflowY="auto">
        {Object.entries(editors).map(renderItem)}
      </Stack>
      <Flex flex="4" overflowY="auto">
        <Listing scope={selectedScope} />
      </Flex>
    </Flex>
  );
};
